import { ToasterService } from '@core_services/toaster.service';
import { AuthService } from '@core_services/auth.service';
import {
  catchError,
  defaultIfEmpty,
  firstValueFrom,
  Observable,
  ObservedValueOf,
  of,
  timeout,
  TimeoutError,
} from 'rxjs';
import {PermissionAccessService} from "@pages/permissions/services/permission-access.service";
import {IPermissionPerCompany} from "@pages/permissions/interfaces";
import {JsonResponse} from "@core_interfaces/JsonResponse.interface";

// Responsible for initializing the permissions of the authenticated user before the application loads.
// This is to ensure that the user has the necessary permissions to access the application's features.
export function initializeAuthUserPermissionsFactory(
  authService: AuthService,
  permissionAccessService: PermissionAccessService,
  toasterService: ToasterService,
): () => Promise<void> {
  return async (): Promise<void> => {
    try {
      if (!authService.authenticated) return Promise.resolve();

      const response:
        | ObservedValueOf<Observable<{ data: IPermissionPerCompany[] }>>
        | JsonResponse = await firstValueFrom(
        permissionAccessService.initializeAuthUserPermissions().pipe(
          timeout(10000), // Timeout the promise after 10 seconds in order for the user to see the UI instead of blank page
          defaultIfEmpty({ data: [] }),
          catchError(
            (
              error: any,
            ): Observable<{
              data: IPermissionPerCompany[];
            }> => {
              if (error instanceof TimeoutError) {
                toasterService.error(
                  'Failed to load permissions',
                  `Request timed out. Refresh the page and if the issue persists, check your internet connection.`,
                );

                console.error(error);
              } else {
                toasterService.error('', 'Failed to load permissions');
              }

              return of({ data: [] });
            },
          ),
        ),
      );

      permissionAccessService.updateAuthUserPermissions(response.data);
    } catch (error) {
      console.error('Failed to load permissions', error);
    }
  };
}
